/**
 * Country List Dropdown.
 * @format
 */

import React, { useState } from 'react';
import Translate from '@components/translation/translate-text-wrapper';
import { useSafeTranslate } from '@hooks/use-safe-translate';
import { InputProperties, FieldProperties } from '../form-modals';
import { contactFormFields } from '../../form-types/contact-form/contact-form-fields';
import DependentFields from './dependent-fields';
import { getCountryFields } from '../form-services';
interface ListItemProperties {
  name: string;
  value: string;
}

/**
 * List Dropdown.
 */
const SelectList = (props: InputProperties): JSX.Element | null => {
  const {
    fieldProperties,
    register,
    errors,
    fieldId,
    selector,
    dependentDropdown = false,
  } = props;
  const {
    fieldTitle,
    fieldName,
    placeholderText,
    id,
    list,
    useNameAsLookupValue,
    errorMessage,
    required,
    parentItems,
    dependencyId,
    dependentField,
  }: FieldProperties = fieldProperties;

  const selectId = fieldId || id;
  const listName = useSafeTranslate(fieldTitle, true);
  const placeholderListText = useSafeTranslate(placeholderText, true);
  const [selected, setSelected] = useState<string>('none');
  const [dependency, setDependency] = useState<null | JSX.Element>(null);
  const fieldList = dependentField && list[selector] ? list[selector] : list;

  /**
   * List Dependency Check.
   */
  const dependencyCheck = (selectValue: string): void => {
    setSelected(selectValue);
    if (parentItems && selectValue !== 'none') {
      if (
        parentItems.filter((element) => element.value === selectValue).length >
        0
      ) {
        // Use standard contact fields to filter on since all dependent fields
        // are captured there.
        const filterDependency = contactFormFields.filter(
          (field) => field.id === dependencyId
        );
        // Set country specific options
        const updatedDependency = getCountryFields(
          filterDependency?.[0],
          selectValue
        );

        // Special case adding postal code for USA only
        const filterPostalCode = contactFormFields.filter(
          (field) =>
            selectValue === 'United States' && field.id === 'postal-code'
        );
        const postalCode = filterPostalCode?.[0] ?? null;

        setDependency({
          fields: [updatedDependency, postalCode],
          register,
          errors,
          selectValue,
        });
      } else {
        setDependency(null);
      }
    }
  };

  /**
   * Render.
   */
  if (!dependentField || (dependentField && dependentDropdown)) {
    return (
      <>
        <label htmlFor={selectId} className={required ? 'required' : ''}>
          {listName}
        </label>
        <select
          id={selectId}
          name={fieldName}
          className={`${selected === 'none' ? 'default-selected' : ''} ${
            errors[fieldName] ? 'error' : ''
          }`}
          data-dependency={dependency}
          {...register(fieldName, {
            required: required,
            onChange: (event) => dependencyCheck(event.target.value),
          })}>
          <option value="" defaultValue>
            {placeholderListText}
          </option>

          {fieldList &&
            fieldList.length > 0 &&
            fieldList.map(
              (listItem: ListItemProperties, index: number): JSX.Element => {
                const { name, value } = listItem;
                const itemValue = useNameAsLookupValue ? name : value;
                return (
                  <Translate
                    key={index}
                    htmlTag="option"
                    optionValue={itemValue}>
                    {name}
                  </Translate>
                );
              }
            )}
        </select>

        {errors[fieldName] && (
          <p className="error-message">
            <Translate>{errorMessage}</Translate>
          </p>
        )}

        {dependency && <DependentFields props={dependency} />}
      </>
    );
  }
  return null;
};
export default SelectList;
